/**
 * High-variant compatible variant selector
 *
 * This implementation uses Shopify's new product_option_value API and Section Rendering API
 * to support products with more than 250 variants. Key differences from traditional approach:
 *
 * 1. Uses option_value_id instead of variant matching
 * 2. Leverages Section Rendering API with option_values parameter
 * 3. Server-side variant filtering instead of client-side
 * 4. Supports combined listings (multi-product variants)
 *
 * Flow:
 * - User selects option -> onVariantChange()
 * - Collect option_value_ids from inputs
 * - Fetch section with ?option_values=id1,id2,id3
 * - Server returns updated section with new availability/pricing
 * - Replace DOM elements with fresh data
 *
 * More information: https://shopify.dev/docs/storefronts/themes/product-merchandising/variants/support-high-variant-products
 */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this.onVariantChange);
  }

  async onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateURL();
    await this.renderProductInfo();

    if (!this.currentVariant) {
      this.toggleAddButton(true, window.variantStrings.unavailable);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateVariantInput();
      this.dispatchVariantChange();
      this.toggleSaleBadge();
    }
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
  }

  updateMasterId() {
    this.currentVariant = null;
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent("variant-change", {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;

    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );

    if (!newMedia) return;

    const parent = newMedia.parentElement;

    parent.prepend(newMedia);

    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    const selectedInputs = this.querySelectorAll('input[type="radio"]:checked, select');
    const optionValueIds = Array.from(selectedInputs)
      .map(input => {
        if (input.tagName === 'SELECT') {
          const selectedOption = input.options[input.selectedIndex];
          return selectedOption?.dataset.optionValueId;
        }
        return input.dataset.optionValueId;
      })
      .filter(id => id);

    if (optionValueIds.length > 0) {
      const url = `${this.dataset.url}?option_values=${optionValueIds.join(',')}`;
      window.history.replaceState({}, "", url);
    }
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');

      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  toggleSaleBadge() {
    const saleBadge = document.querySelector(
      ".product-info .product__badge--sale"
    );
    if (!saleBadge) return;

    const discount =
      this.currentVariant.compare_at_price - this.currentVariant.price;
    const discountPercentage = Math.round(
      (discount / this.currentVariant.compare_at_price) * 100.0
    );

    if (
      discount > 0 &&
      this.currentVariant &&
      this.currentVariant.compare_at_price !== null
    ) {
      saleBadge.textContent = `-${discountPercentage}%`;
      saleBadge.classList.remove("hidden");
    } else {
      saleBadge.classList.add("hidden");
    }
  }

  /**
   * Fetches updated product section using new option_values parameter
   * This replaces the old variant-specific AJAX calls and supports high-variant products
   */
  async renderProductInfo() {
    try {
      // Collect selected option_value_ids from inputs and selects (new high-variant approach)
      const selectedInputs = this.querySelectorAll('input[type="radio"]:checked, select');
      const optionValueIds = Array.from(selectedInputs)
        .map(input => {
          if (input.tagName === 'SELECT') {
            const selectedOption = input.options[input.selectedIndex];
            return selectedOption?.dataset.optionValueId;
          }
          return input.dataset.optionValueId;
        })
        .filter(id => id);

      const params = new URLSearchParams();
      params.set('section_id', this.dataset.section);

      if (optionValueIds.length > 0) params.set('option_values', optionValueIds.join(','));
      const response = await fetch(`${this.dataset.url}?${params}`);

      if (!response.ok) throw new Error("Network response was not ok.");

      const dynamicAddButton = document.querySelector(
        ".shopify-payment-button"
      );

      /* NOTE:
        DOM elements cannot be optional, only hidden.
        To ensure that elements are updated correctly the liquid must
        contain the optional elements, then hide or make visible based
        on the response.
      */
      const domToUpdate = [
        [
          `#price-${this.dataset.section}`,
          (destPrice, srcPrice) => {
            if (srcPrice) destPrice.innerHTML = srcPrice.innerHTML;

            destPrice.classList.remove("visibility-hidden");
          },
        ],
        [
          `#product-form-${this.dataset.section} input[name="inventory_quantity"]`,
          (destQtyInput, srcQtyInput) => {
            if (!srcQtyInput) return;

            destQtyInput.value = srcQtyInput.value;
          },
        ],
        [
          "[data-preorder-enabled]",
          (destPreorder, srcPreorder) => {
            if (!srcPreorder) return;

            const isPreorder =
              srcPreorder.getAttribute("data-preorder-enabled") === "true";

            destPreorder.setAttribute("data-preorder-enabled", isPreorder);
            destPreorder.value = srcPreorder.value;

            if (isPreorder) {
              destPreorder.removeAttribute("disabled");
              dynamicAddButton?.classList.add("hidden");
            } else {
              destPreorder.setAttribute("disabled", true);
              dynamicAddButton?.classList.remove("hidden");
            }

            // Update button class for preorder state
            const addButton = document.querySelector(`#product-form-${this.dataset.section} button[name="add"]`);
            if (addButton) {
              if (isPreorder) {
                addButton.classList.add('button--preorder');
              } else {
                addButton.classList.remove('button--preorder');
              }
            }
          },
        ],
        [
          "[data-variant-text]",
          (destText, srcText) => {
            if (!srcText) return;

            destText.innerHTML = srcText.innerHTML;
            destText.classList.toggle(
              "visibility-hidden",
              srcText.classList.contains("visibility-hidden")
            );
          },
        ],
        [
          "[data-inventory-message]",
          (destInv, srcInv) => {
            const message = srcInv.getAttribute(
              "data-inventory-message-content"
            );

            if (message) {
              destInv.innerHTML = srcInv.innerHTML;
              destInv.classList.remove("visibility-hidden");
              destInv.setAttribute("data-inventory-message-content", message);
            } else {
              destInv.innerHTML = "";
              destInv.classList.add("visibility-hidden");
              destInv.setAttribute("data-inventory-message-content", "");
            }
          },
        ],
      ];

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, "text/html");

      domToUpdate.forEach(([selector, cb]) => {
        const destElms = Array.from(document.querySelectorAll(selector));
        const srcElms = Array.from(html.querySelectorAll(selector));

        destElms.forEach((destElm, index) => {
          const srcElm = srcElms[index];

          cb?.(destElm, srcElm);
        });
      });

      this.currentVariant = JSON.parse(
        html.querySelector('script[type="application/json"]')?.textContent || '{}'
      );

      this.updateOptionAvailability(html);

      if (this.currentVariant) {
        this.toggleAddButton(
          !this.currentVariant.available,
          window.variantStrings.soldOut
        );
      }
    } catch (error) {
      console.log(error);
    }
  }

  updateOptionAvailability(html) {
    const srcInputs = html.querySelectorAll('.options__input[data-option-value-id]');

    srcInputs.forEach(srcInput => {
      const optionValueId = srcInput.dataset.optionValueId;
      const isAvailable = srcInput.dataset.optionAvailable !== 'false';

      const destInput = this.querySelector(`.options__input[data-option-value-id="${optionValueId}"]`);
      if (!destInput) return;

      const label = this.querySelector(`label[for="${destInput.id}"]`);
      if (label) {
        label.classList.toggle('options__label--disabled', !isAvailable);
      }
    });
  }

  toggleAddButton(disable = true, text = "") {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');
    const addButtonText = addButton.querySelector(
      "[data-add-to-cart-button-text]"
    );

    // If the variant is available(with stock or preorder) then the button should be enabled
    const availableVariantText =
      document.querySelector(
        `#product-form-${this.dataset.section} [data-preorder-enabled]`
      ).dataset.preorderEnabled === "true"
        ? window.variantStrings.preorder
        : window.variantStrings.addToCart;

    if (!addButton || !addButtonText) return;

    if (disable) {
      this.closest("product-info")
        ? this.closest("product-info")
            .querySelector("[data-inventory-message]")
            .classList.add("visibility-hidden")
        : null;

      addButton.setAttribute("disabled", true);
      if (text) addButtonText.textContent = text;
    } else {
      this.closest("product-info")
        ? this.closest("product-info")
            .querySelector("[data-inventory-message]")
            .classList.remove("visibility-hidden")
        : null;

      addButton.removeAttribute("disabled");
      addButtonText.textContent = availableVariantText;
    }
  }

  setUnavailable() {
    const addButton = document
      .getElementById(`product-form-${this.dataset.section}`)
      ?.querySelector('[name="add"]');

    const addButtonText = addButton.querySelector(
      "[data-add-to-cart-button-text]"
    );
    if (!addButton) return;

    addButtonText.textContent = window.variantStrings.unavailable;

    document
      .getElementById(`price-${this.dataset.section}`)
      ?.classList.add("visibility-hidden");
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });
  }
}

if (!customElements.get("variant-radios")) {
  customElements.define("variant-radios", VariantRadios);
}
